import React, {useCallback, useMemo, useState} from 'react';

import {NOTIFICATION_TIMEOUTS} from 'src/constants';
import {PB_ADMIN} from 'src/hooks/access-resource-constant';
import {useApiWithNotifications} from 'src/hooks/api-with-notification/useApiWithNotifications';
import {useCurrentUser} from 'src/hooks/useCurrentUser';
import {deleteApprovalRule} from 'src/openapi/admin/apis/ApprovalRuleApi';
import {VendorApprovalRule} from 'src/openapi/admin/models/VendorApprovalRule';

import {FlatButton} from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import Icon, {Icons} from '@pb/kit-component-icon';
import Loading from '@pb/kit-component-loading';
import {Message} from '@pb/kit-component-message';
import {Tooltip} from '@pb/kit-component-popups';
import Typography from '@pb/kit-component-typography';
import {useShown} from '@pb/kit-hooks-use-shown';

import {section} from '../vendor-profile.module.styl';

import {AddRuleDialog} from './rules/add-rule-dialog';
import {DeleteRuleDialog} from './rules/delete-rule-dialog';
import {RulesTable} from './rules/rules-table';

import type {VendorRulesState} from '../hooks/use-vendor-rules';
import type {Vendor} from 'src/openapi/admin/models/Vendor';

type Props = {
    vendor?: Vendor | null,
    vendorRules: VendorRulesState,
};

const RULES_EMPTY_MESSAGE = 'There are currently no rules associated with this Vendor. Please use the "Add Rule" button to create a new rule.';
const MAX_RULES_PER_VENDOR = 10;

export const RulesSection: React.FC<Props> = ({vendor, vendorRules}) => {
    const {shown, onToggle, onClose} = useShown();
    const {executeWithNotifications} = useApiWithNotifications();
    const {
        rules,
        loadingRules,
        loadingGrants,
        error,
        grants,
        addRuleToState,
        removeRuleFromState,
        reloadGrants,
    } = vendorRules;
    const [deletingMap, setDeletingMap] = useState<Record<string, boolean>>({});
    const [ruleToDelete, setRuleToDelete] = useState<string | null>(null);
    const [deleteBusy, setDeleteBusy] = useState(false);

    const handleOpen = useCallback(() => onToggle(), [onToggle]);
    const handleClose = useCallback(() => onClose(), [onClose]);

    const rulesCountLabel = useMemo(() => (
        rules.length > 0 ? ` (${rules.length})` : ''
    ), [rules.length]);

    const isAddDisabled = !vendor || rules.length >= MAX_RULES_PER_VENDOR;
    const {hasAccessToResource} = useCurrentUser();
    const canAddRule = hasAccessToResource(PB_ADMIN.CREATE_RULE);

    const handleRuleCreated = useCallback((rule: VendorApprovalRule) => {
        addRuleToState(rule);
        reloadGrants();
    }, [addRuleToState, reloadGrants]);

    const handleDeleteClick = useCallback((ruleId: string) => {
        setRuleToDelete(ruleId);
    }, []);

    const handleDeleteClose = useCallback(() => {
        if (!deleteBusy) {
            setRuleToDelete(null);
        }
    }, [deleteBusy]);

    const handleConfirmDelete = useCallback(async () => {
        if (!ruleToDelete) {
            return;
        }

        setDeleteBusy(true);
        setDeletingMap(prev => ({...prev, [ruleToDelete]: true}));

        try {
            const response = await executeWithNotifications<undefined>(
                deleteApprovalRule({ruleId: ruleToDelete}),
                {
                    success: 'Rule successfully deleted.',
                    error: 'Unable to delete rule. Please try again.',
                    exception: 'Unable to delete rule. Please try again.',
                },
                'delete-approval-rule',
                NOTIFICATION_TIMEOUTS,
            );

            if (response.success) {
                removeRuleFromState(ruleToDelete);
                reloadGrants();
            }
        } finally {
            setDeletingMap(prev => {
                const next = {...prev};
                if (ruleToDelete) {
                    delete next[ruleToDelete];
                }
                return next;
            });
            setDeleteBusy(false);
            setRuleToDelete(null);
        }
    }, [executeWithNotifications, reloadGrants, removeRuleFromState, ruleToDelete]);

    const renderAddButton = () => {
        if (!canAddRule) {
            return null;
        }

        if (isAddDisabled) {
            return (
                <Tooltip
                    placement="bottom"
                    color="light"
                    anchor={({anchorRef, getAnchorProps}) => (
                        <FlatButton
                            startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                            disabled={true}
                            ref={anchorRef}
                            {...getAnchorProps()}>
                            <b>Add Rule</b>
                        </FlatButton>
                    )}>
                    The vendor profile already contains the maximum allowed number of rules
                </Tooltip>
            );
        }

        return (
            <FlatButton
                startIcon={<Icon type={Icons.PLUS_CIRCLE}/>}
                onClick={handleOpen}>
                <b>Add Rule</b>
            </FlatButton>
        );
    };

    const renderContent = () => {
        if (loadingRules || (rules.length === 0 && loadingGrants)) {
            return (
                <Group direction="h" alignItems="center" gap="xs">
                    <Loading type="ICON"/>
                    <Typography>Loading rules...</Typography>
                </Group>
            );
        }

        if (rules.length === 0) {
            return (
                <Typography fontWeight="semi-bold">
                    {RULES_EMPTY_MESSAGE}
                </Typography>
            );
        }

        return (
            <RulesTable
                rules={rules}
                deletingMap={deletingMap}
                onDelete={handleDeleteClick}
            />
        );
    };

    if (!vendor) {
        return (
            <div className={section}>
                <Group direction="v" gap="m" style={{padding: 16}}>
                    <Group direction="h" alignItems="center" gap="s">
                        <Typography fontWeight="semi-bold" variant="heading-level-6">
                            Rules
                        </Typography>
                    </Group>
                    <Group direction="h" alignItems="center" gap="xs">
                        <Loading type="ICON"/>
                        <Typography>Loading rules...</Typography>
                    </Group>
                </Group>
            </div>
        );
    }

    return (
        <div className={section}>
            <Group direction="v" gap="m" style={{padding: 16}}>
                <Group direction="h" alignItems="center" gap="s">
                    <Group direction="h" alignItems="center" gap="s">
                        <Typography fontWeight="semi-bold" variant="heading-level-6">
                            {`Rules${rulesCountLabel}`}
                        </Typography>
                    </Group>
                    {renderAddButton()}
                </Group>

                {error ? (
                    <Message color="error">
                        Unable to load rules. Please try again.
                    </Message>
                ) : null}

                {renderContent()}
            </Group>
            <AddRuleDialog
                shown={shown}
                onClose={handleClose}
                vendorId={vendor?.id}
                existingRules={rules}
                availableGrants={grants}
                onRuleCreated={handleRuleCreated}
            />
            <DeleteRuleDialog
                shown={ruleToDelete !== null}
                onClose={handleDeleteClose}
                onConfirm={handleConfirmDelete}
                busy={deleteBusy}
            />
        </div>
    );
};
